import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Container,
  CircularProgress,
  Grid,
  Button,
  Stack,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";
import ProsManagementEdit from "./Pros_management_edit";

// =================================================
// type
// =================================================
type Row = {
  id: number;
  Company_name: string;
  Sales_record_id: number;
  contact_day: string;
  Service_details: string;
  Lead_sources: string;
  Contact_person: string;
  Proposal_details: string;
  Sales_person: string;
  Sales_approach_method: string;
  Sales_no: number;
  Sales_review: string;

  company_id?: number | "";
  sales_person_id?: number | "";
  sales_approach_method_id?: number | "";
  lead_sources_id?: number | "";
  proposed_service_id?: number | "";
  purchase_intention_id?: number | "";
  next_action_id?: number | "";
  sales_status_id?: number | "";
  man_hours?: number | string;
};

type ProsSalesDetailsProps = {
  EditOpen: boolean;
  SetEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
  SalesRecordId: number | null;

  // 親一覧の再取得用
  onChanged?: () => void | Promise<void>;
};

// =================================================
// UI Components
// =================================================
const card = {
  p: 2.5,
  mb: 2.5,
  borderRadius: 3,
  border: "1px solid #E5E7EB",
  background: "#fff",
};

const Section = ({ title, children }: any) => (
  <Paper sx={card}>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 14,
        mb: 2,
        color: "#111827",
        borderLeft: "4px solid #6366F1",
        pl: 1,
      }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

const TextArea = ({ label, value }: any) => (
  <Box
    sx={{
      mt: 2,
      p: 2,
      borderRadius: 2,
      border: "1px solid #E5E7EB",
      backgroundColor: "#F9FAFB",
    }}
  >
    <Typography fontSize={12} color="#6B7280" mb={1}>
      {label}
    </Typography>
    <Typography
      fontSize={14}
      color="#111827"
      sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
    >
      {value || "未設定"}
    </Typography>
  </Box>
);

const titleBar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#1E293B",
  color: "#fff",
};

const kpiCard = {
  p: 2,
  mb: 3,
  borderRadius: 3,
  border: "1px solid #E5E7EB",
  background: "#fff",
};

const KPI = ({ label, value }: any) => (
  <Box>
    <Typography fontSize={11} color="#6B7280">
      {label}
    </Typography>
    <Typography fontSize={18} fontWeight={700} color="#111827">
      {value || "未設定"}
    </Typography>
  </Box>
);

const TimelineItem = ({ label, value }: any) => (
  <Box
    sx={{
      borderLeft: "3px solid #6366F1",
      pl: 2,
      mb: 2,
    }}
  >
    <Typography fontSize={12} color="#6B7280">
      {label}
    </Typography>
    <Typography fontSize={14} fontWeight={600}>
      {value || "未設定"}
    </Typography>
  </Box>
);

const Highlight = ({ label, value }: any) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      background: "#EEF2FF",
      border: "1px solid #C7D2FE",
      mb: 2,
    }}
  >
    <Typography fontSize={12} color="#4338CA">
      {label}
    </Typography>
    <Typography fontSize={15} fontWeight={700}>
      {value || "未設定"}
    </Typography>
  </Box>
);

const reviewBox = {
  p: 2,
  borderRadius: 2,
  background: "#FFF7ED",
  border: "1px solid #FDBA74",
};

// =================================================
// Component
// =================================================
const ProsSalesDetails = ({
  EditOpen,
  SetEditOpen,
  SalesRecordId,
  onChanged,
}: ProsSalesDetailsProps) => {
  const loginUserData = useRecoilValue(LoginUserAtom);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Row[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<any>(null);

  const [salesPersonList, setSalesPersonList] = useState<any[]>([]);
  const [approachList, setApproachList] = useState<any[]>([]);
  const [leadList, setLeadList] = useState<any[]>([]);
  const [serviceList, setServiceList] = useState<any[]>([]);
  const [companyList, setCompanyList] = useState<any[]>([]);

  // =================================================
  // useEffect
  // =================================================
  useEffect(() => {
    if (!EditOpen || !SalesRecordId) return;

    setRows([]);
    setErrorMessage(null);
    setEditMode(false);

    const init = async () => {
      setLoading(true);

      await Promise.all([
        fetchData(SalesRecordId),
        fetchMasterData(),
      ]);

      setLoading(false);
    };

    init();
  }, [EditOpen, SalesRecordId]);

  // =================================================
  // API
  // =================================================
  const fetchData = async (salesRecordId: number) => {
    try {
      const postData = {
        flag: "POPUPSalesHistory",
        sales_record_id: salesRecordId,
        access_token: loginUserData.accessToken,
        algorithm: loginUserData.algorithm,
      };

      const res = await axios.post(
        ConstList.getApiUrl() + "ProspectiveManagement.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      const list = res.data?.data?.saleshistory_prosmanagement ?? [];

      const mapped: Row[] = list.map((v: any, i: number) => ({
        id: i + 1,

        Sales_record_id: Number(v.sales_record_id),

        // 表示用
        Company_name: v.customer_name ?? v.company_name ?? "",
        contact_day: v.sales_date ?? "",
        Service_details: v.proposed_service ?? "",
        Lead_sources: v.lead_sources ?? "",
        Contact_person: v.contact_person ?? "",
        Proposal_details: v.sales_summary ?? v.proposal_details ?? "",
        Sales_person: v.sales_person ?? "",
        Sales_approach_method: v.sales_approach_method ?? "",
        Sales_no: Number(v.sales_no ?? 0),
        Sales_review: v.sales_review ?? "",

        // 編集・更新用ID
        company_id: v.company_id ? Number(v.company_id) : "",
        sales_person_id: v.sales_person_id ? Number(v.sales_person_id) : "",
        sales_approach_method_id: v.sales_approach_method_id
          ? Number(v.sales_approach_method_id)
          : "",
        lead_sources_id: v.lead_sources_id ? Number(v.lead_sources_id) : "",
        proposed_service_id: v.proposed_service_id
          ? Number(v.proposed_service_id)
          : "",
        purchase_intention_id: v.purchase_intention_id
          ? Number(v.purchase_intention_id)
          : "",
        next_action_id: v.next_action_id ? Number(v.next_action_id) : "",
        sales_status_id: v.sales_status_id ? Number(v.sales_status_id) : "",
        man_hours: v.man_hours ?? "",
      }));

      setRows(mapped);
      setForm(mapped[0] ?? null);
    } catch (e) {
      console.error(e);
      setErrorMessage("営業履歴の取得に失敗しました。");
    }
  };

  const fetchMasterData = async () => {
    try {
      const res = await axios.post(
        ConstList.getApiUrl() + "Edit_Pros_Management.php",
        {
          flag: "get_master_data",
          access_token: loginUserData.accessToken,
          algorithm: loginUserData.algorithm,
        },
        ConstList.AXIOS_CONFIG
      );

      if (res.data?.status !== 1) {
        console.error("マスタ取得失敗", res.data);
        return;
      }

      setSalesPersonList(res.data?.data?.sales_person_list || []);
      setApproachList(res.data?.data?.approach_list || []);
      setLeadList(res.data?.data?.lead_list || []);
      setServiceList(res.data?.data?.service_list || []);
      setCompanyList(res.data?.data?.company_list || []);
    } catch (e) {
      console.error(e);
      setErrorMessage("マスタデータの取得に失敗しました。");
    }
  };

  // =================================================
  // handlers
  // =================================================
  const handleEdit = () => {
    if (!rows[0]) return;

    setEditMode(true);
    setForm(rows[0]);
  };

  const handleSaved = async () => {
    setEditMode(false);

    if (SalesRecordId) {
      setLoading(true);
      await fetchData(SalesRecordId);
      setLoading(false);
    }

    await onChanged?.();
  };

  const handleDelete = async () => {
    if (!rows[0]) return;

    const confirm = window.confirm("削除しますか？");
    if (!confirm) return;

    try {
      const res = await axios.post(
        ConstList.getApiUrl() + "ProspectiveManagement.php",
        {
          flag: "DeleteSalesHistory",
          sales_record_id: rows[0].Sales_record_id,
          access_token: loginUserData.accessToken,
          algorithm: loginUserData.algorithm,
        },
        ConstList.AXIOS_CONFIG
      );

      const isSuccess =
        res.data?.status === 1 ||
        res.data?.data === true ||
        res.data?.data?.status === 1;

      if (!isSuccess) {
        alert(res.data?.message ?? "削除失敗");
        return;
      }

      await onChanged?.();

      alert("削除しました");
      setEditMode(false);
      SetEditOpen(false);
    } catch (e) {
      console.error(e);
      alert("削除失敗");
    }
  };

  // =================================================
  // JSX
  // =================================================
  return (
    <Dialog
      open={EditOpen}
      fullWidth
      maxWidth="md"
      onClose={() => {
        setEditMode(false);
        SetEditOpen(false);
      }}
    >
      {/* ===== ヘッダー ===== */}
      <DialogTitle sx={titleBar}>
        <Box>
          <Typography fontSize={16} fontWeight={700}>
            {rows[0]?.Company_name || "未設定"}
          </Typography>
          <Typography fontSize={12} sx={{ opacity: 0.7 }}>
            営業日報 / #{rows[0]?.Sales_record_id}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          {!editMode && (
            <>
              <Button
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ color: "#ffffff" }}
              >
                編集
              </Button>

              <Button
                startIcon={<DeleteIcon />}
                color="error"
                onClick={handleDelete}
              >
                削除
              </Button>
            </>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: "#F6F8FB",
          py: 3,
          "& .MuiTypography-root": {
            color: "#111827 !important",
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : errorMessage ? (
          <Typography color="error">{errorMessage}</Typography>
        ) : editMode ? (
          <ProsManagementEdit
            form={form}
            setForm={setForm}
            onSaved={handleSaved}
            onCancel={() => setEditMode(false)}
            salesPersonList={salesPersonList}
            companyList={companyList}
            approachList={approachList}
            leadList={leadList}
            serviceList={serviceList}
          />
        ) : (
          <Container maxWidth="md">
            {/* ===== KPIサマリー ===== */}
            <Paper sx={kpiCard}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <KPI label="営業回数" value={rows[0]?.Sales_no} />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <KPI label="営業担当" value={rows[0]?.Sales_person} />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <KPI label="接触日" value={rows[0]?.contact_day} />
                </Grid>
              </Grid>
            </Paper>

            {/* ===== アクションタイムライン ===== */}
            <Section title="営業アクション">
              <TimelineItem
                label="営業方法"
                value={rows[0]?.Sales_approach_method}
              />
              <TimelineItem
                label="きっかけ"
                value={rows[0]?.Lead_sources}
              />
              <TimelineItem
                label="顧客担当者"
                value={rows[0]?.Contact_person}
              />
            </Section>

            {/* ===== 提案内容 ===== */}
            <Section title="提案内容">
              <Highlight label="提案サービス" value={rows[0]?.Service_details} />
              <TextArea label="提案詳細" value={rows[0]?.Proposal_details} />
            </Section>

            {/* ===== 振り返り ===== */}
            <Section title="振り返り・所感">
              <Box sx={reviewBox}>
                <TextArea label="営業レビュー" value={rows[0]?.Sales_review} />
              </Box>
            </Section>
          </Container>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProsSalesDetails;
